import Vue from 'vue'
import Vuex from 'vuex'
import VuexPersist from 'vuex-persist'
import eventBus, {
  EVENT_LOGIN_AFTER,
  EVENT_LOGOUT_AFTER,
  EVENT_PUSHER_AVAILABLE,
  EVENT_ACCESS_TOKEN_UPDATED,
  EVENT_GA_AVAILABLE,
  EVENT_GAMEINSTANCE_UPDATED,
  EVENT_PUSHER_EVENT_RECEIVED,
  EVENT_EXT_NATIVE_APP_READY,
  EVENT_EXT_NATIVE_APP_NOTIFICATION_INFO,
  EVENT_EXT_NATIVE_APP_PUSH_NOTIFICATION_INFO,
} from '@/plugins/eventbus'
import sjcl from 'sjcl'
import axios from '@/plugins/http'
import jwt from 'jsonwebtoken'
import i18n from '@/plugins/i18n'
import $common from '@/plugins/common'
import {
  EVENT_SHOW_PREMIUM_OPTIONS,
  EVENT_SHOW_COINS_DIALOG,
  EVENT_SHOW_ONLINE_IDS_DIALOG,
  EVENT_STORE_ENV_UPDATED,
} from './eventbus'

Vue.use(Vuex)

const MAX_LOG_ENTRIES = 30

export const MUTATION_INCREMENT = 'increment'
export const MUTATION_INIT = 'init'
export const MUTATION_ON_ERROR = 'onError'
export const MUTATION_LOG = 'log'
export const MUTATION_LOGIN = 'login'
export const MUTATION_CLEAR_STATE = 'clearState'
export const MUTATION_LOGOUT = 'logout'
export const MUTATION_REGISTERED = 'registered'
export const MUTATION_CHANGE_EMAIL = 'changeEMail'
export const MUTATION_SERVICE_WORKER_UPDATED = 'serviceWorkerUpdated'
export const MUTATION_SERVICE_WORKER_UPDATECONFIRMED = 'serviceWorkerUpdateConfirmed'
export const MUTATION_SERVICE_WORKER_HAS_BEEN_UPDATED = 'serviceWorkerHasBeenUpdated'
export const MUTATION_RECALC_LOGIN_STATE = 'recalcLoginState'
export const MUTATION_START_LOADING = 'startLoading'
export const MUTATION_STOP_LOADING = 'stopLoading'
export const MUTATION_RAISE_NOTIFICATION = 'raiseNotification'
export const MUTATION_RAISE_NEWSLETTER_MODAL = 'raiseNewsletterModal'
export const MUTATION_DISMISS_NOTIFICATION = 'dismissNotification'
export const MUTATION_ACTIVE_GAMES_LOADED = 'activeGamesLoaded'
export const MUTATION_PLATFORMS_LOADED = 'platformsLoaded'
export const MUTATION_GAMES_LOADED = 'gamesLoaded'
export const MUTATION_FRIENDS_LOADED = 'friendsLoaded'
export const MUTATION_PROFILE_LOADED = 'profileLoaded'
export const MUTATION_COIN_ACCOUNT_LOADED = 'coinAccountLoaded'
export const MUTATION_FRIENDSHIP_REQUESTS_LOADED = 'friendshipRequestsLoaded'
export const MUTATION_PERFORM_SUBSCRIPION = 'performSubscription'
export const MUTATION_CANCELED_OPEN_SUBSCRIPION = 'cancelPerformSubscription'
export const MUTATION_SHOW_PREMIUM_DIALOG = 'showPremiumDialog'
export const MUTATION_HIDE_PREMIUM_DIALOG = 'hidePremiumDialog'
export const MUTATION_SHOW_COINS_DIALOG = 'showCoinsDialog'
export const MUTATION_SHOW_ONLINE_IDS_DIALOG = 'showOnlineIdsDialog'
export const MUTATION_HIDE_ONLINE_IDS_DIALOG = 'hideOnlineIdsDialog'
export const MUTATION_SHOW_USER_COUNTRY_DIALOG = 'showUserCountryDialog'
export const MUTATION_HIDE_USER_COUNTRY_DIALOG = 'hideUserCountryDialog'
export const MUTATION_HIDE_COINS_DIALOG = 'hideCoinsDialog'
export const MUTATION_SHOW_CHECKOUT_DIALOG = 'showCheckoutDialog'
export const MUTATION_HIDE_CHECKOUT_DIALOG = 'hideCheckoutDialog'
export const MUTATION_PRESET_GAME = 'presetGame'
export const MUTATION_PRESET_PLATFORM = 'presetPlatform'
export const MUTATION_ENV_LOADED = 'envLoaded'
export const MUTATION_ADVERTISING_LOADED = 'advertisingLoaded'
export const MUTATION_SET_PUSHER_APP_KEY = 'setPusherAppKey'
export const MUTATION_SET_GA_CODE = 'setGoogleAnalyticsCode'
export const MUTATION_GAMEINSTANCE_UPDATED = 'gameInstanceUpdated'
export const MUTATION_CHAT_GROUP_STATE_UPDATED = 'chatGroupStateUpdated'
export const MUTATION_GAME_INVITATIONS_LOADED = 'gameInvitationsLoaded'
export const MUTATION_ADD_GAME_INSTANCE_INVITATION = 'addGameInvitation'
export const MUTATION_REMOVE_GAME_INSTANCE_INVITATION = 'removeGameInvitation'
export const MUTATION_SET_FORCE_ADVERTISING = 'setForceAdvertising'
export const MUTATION_HIDE_COIN_FEATURE = 'hideCoinFeature'
export const MUTATION_SET_UI_OPTION = 'setUiOption'
export const MUTATION_COMPOSITE_DATA_LOADED = 'compositeDataLoaded'
export const MUTATION_SET_MISSING_USER_DATA = 'setMissingUserData'
export const MUTATION_UPDATE_COOKIE_CONSENT_STATE = 'updateCookieConsentState'

export const MUTATION_EXT_NATIVE_APP_READY = 'extNativeAppReady'
export const MUTATION_EXT_NATIVE_APP_NOTIFICATION_INFO= 'ext.nativeAppNotificationInfo'
export const MUTATION_EXT_NATIVE_APP_PUSH_NOTIFICATION_INFO= 'extNativeAppPushNotificationInfo'

export const ACTION_INIT = 'initApp'
export const ACTION_LOAD_PUBLIC_ALL = 'loadPublicAll'
export const ACTION_LOAD_PLATFORMS = 'loadPlatforms'
export const ACTION_LOAD_GAMES = 'loadGames'
export const ACTION_LOAD_PROFILE = 'loadProfile'
export const ACTION_RELOAD_ACTIVE_GAMES = 'reloadActiveGameinstances'
export const ACTION_LOAD_FRIENDS = 'loadFriends'
export const ACTION_RELOAD_DATA = 'reloadData'
export const ACTION_LOAD_ALL_USER_INFORMATION = 'loadAllUserInformation'
export const ACTION_CLAIM_PUSH_TOKEN = 'claimPushToken'
export const ACTION_LOAD_COIN_ACCOUNT = 'loadCoinAccount'
export const ACTION_COMPOSITE_DATA_LOADED = 'compositeDataLoaded'

const updateAccessToken = function (accessToken) {
  if (accessToken) {
    axios.defaults.headers.common['Authorization'] = 'Bearer ' + accessToken
  } else {
    delete axios.defaults.headers.common['Authorization']
  }
  Vue.nextTick().then(() => {
    eventBus.$emit(EVENT_ACCESS_TOKEN_UPDATED, accessToken)
  })
}

const vuexPersist = new VuexPersist({
  key: 'frontend',
  storage: localStorage,
  reducer: (state) => ({
    count: state.count,
    log: state.log,
    auth: state.auth,
    serviceWorker: state.serviceWorker,
    activeGames: state.activeGames,
    newsletterModal: false,
    platforms: state.platforms,
    games: state.games,
    friends: state.friends,
    friendshipRequests: state.friendshipRequests,
    gameInvitations: state.gameInvitations,
    profile: state.profile,
    presets: state.presets,
    env: {
      global: state.env.global,
      user: state.env.user,
    },
    advertising: state.advertising,
    coinSystem: {
      hideCoinFeature: state.coinSystem.hideCoinFeature,
    },
    ui: state.ui
  })
})

const store = new Vuex.Store({
  plugins: [vuexPersist.plugin],
  state: {
    count: 0,
    log: {
      content: [] // {message: string, createdAt: iso-date-time-string}
    },
    serviceWorker: {
      updated: false,
      lastUpdateDate: null
    },
    auth: {
      emailAddress: null,
      accessTokenEncrypted: null,
    },
    activeGames: [],
    platforms: [],
    games: [],
    friends: [],
    friendsLoadStartAt: undefined, // undefined or a Date object when the friends load (via api) has been initiated
    friendshipRequests: [],
    gameInvitations: [],
    profile: null,
    isLoggedIn: false, // non-persistent
    serviceWorkerWaiting: undefined,
    loadingCounter: 0,
    isLoading: false,
    showPremiumDialog: false,
    showCoinsDialog: false,
    showOnlineIdsDialog: false,
    showUserCountryDialog: false,
    checkoutDialogData: undefined,
    performSubscription: undefined,
    presets: {
      game: undefined,
      platform: undefined,
    },
    env: {
      global: {
        isProduction: true,
      },
      user: {},
    },
    volatile: {
      pusherAppKey: undefined,
      ga: undefined,
    },
    nativeApp: {
      ready: false,
      type: undefined, // e.g. ios, android
      notificationsEnabled: undefined,
      pushToken: undefined,
    },
    advertising: {
      banner: {
        enabled: false,
        transitionSpeed: 600,
        duration: 8000,
        aspectRatio: 265/1160,
        media: [] // list of {url: "", link: ""}
      },
      forceAdvertising: false,
    },
    coinSystem: {
      account: undefined,
      hideCoinFeature: false,
    },
    ui: {
      options: {},
    },
    missingUserData:{missingFields:[]},
    cookieConsent: {},

    /*
    Notification format
    {
        type: error|warning|info|success,  // used as theme colors and to determine timeout
        message: string,
        status: http status,
        [code: error code?]
        details: // anything returned from the API for now
    }
    */
    notification: undefined,
  },
  mutations: {
    [MUTATION_INIT](state) {
      if (state.auth.accessTokenEncrypted) {
        let accessToken = sjcl.decrypt("local.storage", state.auth.accessTokenEncrypted)
        updateAccessToken(accessToken)
      }
      this.commit(MUTATION_RECALC_LOGIN_STATE)
      this.dispatch(ACTION_INIT)
      if (state.serviceWorker.showUpdatedMessage) {
        this.commit(MUTATION_SERVICE_WORKER_HAS_BEEN_UPDATED);
      }
    },
    [MUTATION_INCREMENT](state) {
      state.count++
      eventBus.$emit('count')
    },
    [MUTATION_LOG](state, msg) {
      console.log(msg)
      var entry = {
        message: msg,
        createdAt: new Date().toISOString()
      }
      var newContent = JSON.parse(JSON.stringify(state.log.content))
      newContent.push(entry)
      if (newContent.length > MAX_LOG_ENTRIES) {
        newContent = newContent.slice(newContent.length - MAX_LOG_ENTRIES, newContent.length)
      }
      state.log.content = newContent
    },
    [MUTATION_SERVICE_WORKER_UPDATED](state, waitingServiceWorker) {
      this.commit(MUTATION_LOG, 'New app-content is available')
      state.serviceWorker.waiting = waitingServiceWorker
      state.serviceWorker.updated = true
      state.serviceWorkerWaiting = waitingServiceWorker
      // update automatically
      window.setTimeout(() => {
        this.commit(MUTATION_SERVICE_WORKER_UPDATECONFIRMED)
      }, 1)
    },
    [MUTATION_SERVICE_WORKER_UPDATECONFIRMED](state) {
      this.commit(MUTATION_LOG, 'Activating new service worker & reloading app content')
      state.serviceWorker.updated = false
      state.serviceWorker.showUpdatedMessage = true
      state.serviceWorker.lastUpdateDate = new Date().toISOString()
      if (state.serviceWorkerWaiting) {
        state.serviceWorkerWaiting.postMessage({
          type: 'SKIP_WAITING'
        });
        state.serviceWorkerWaiting = undefined
      }
      window.setTimeout(() => {
        location.reload()
      }, 1)
    },
    [MUTATION_SERVICE_WORKER_HAS_BEEN_UPDATED](state) {
      state.serviceWorker.showUpdatedMessage = false
      this.commit(MUTATION_RAISE_NOTIFICATION, {
        type: "info",
        message: i18n.t('common.app-updated-successfully')
      })
    },
    [MUTATION_ENV_LOADED](state, data) {
      // console.log('env loaded', data)
      state.env.global = data.global
      this.commit(MUTATION_SET_PUSHER_APP_KEY, state.env.global.pusher ? state.env.global.pusher.app_key : null)
      this.commit(MUTATION_SET_GA_CODE, state.env.global.googleAnalyticsProperty)
      if (data.user) {
        state.env.user = data.user
      }
      state.env._loaded = true
      eventBus.$emit(EVENT_STORE_ENV_UPDATED, {env: state.env})
    },
    [MUTATION_ADVERTISING_LOADED](state, data) {
      if (data.banner.enabled !== undefined) {
        state.advertising.banner.enabled = data.banner.enabled;
      }
      if (data.banner.transitionSpeed !== undefined) {
        state.advertising.banner.transitionSpeed = data.banner.transitionSpeed;
      }
      if (data.banner.duration !== undefined) {
        state.advertising.banner.duration = data.banner.duration;
      }
      if (data.banner.media !== undefined) {
        state.advertising.banner.media = data.banner.media;
      }
      if (data.banner.aspectRatio !== undefined) {
        state.advertising.banner.aspectRatio = data.banner.aspectRatio;
      }
    },
    [MUTATION_COIN_ACCOUNT_LOADED](state, data) {
      state.coinSystem.account = data;
    },
    [MUTATION_SET_FORCE_ADVERTISING](state, enabled) {
      state.advertising.forceAdvertising = enabled == true;
    },
    [MUTATION_HIDE_COIN_FEATURE](state, enabled) {
      state.coinSystem.hideCoinFeature = enabled == true;
    },
    [MUTATION_SET_UI_OPTION](state, data) {
      state.ui.options[data.key] = data.value;
    },
    [MUTATION_RECALC_LOGIN_STATE](state) {
      var isLoggedIn = (state.auth.accessTokenEncrypted ? true : false)
      if (isLoggedIn) {
        var token = sjcl.decrypt("local.storage", state.auth.accessTokenEncrypted)
        var decoded = jwt.decode(token, {
          complete: true
        })
        if (decoded && decoded.payload) {
          // console.log(decoded.payload)
          var expireClientDate = new Date(0);
          expireClientDate.setUTCSeconds(decoded.payload.exp)
          // console.log('Now    : ' + new Date().toLocaleString())
          // console.log('Expires: ' + expireClientDate.toLocaleString())
          if (expireClientDate && expireClientDate.getTime() < new Date().getTime()) {
            isLoggedIn = false
            this.commit(MUTATION_LOG, 'Token expired');
            state.auth.accessTokenEncrypted = null
            state.profile = null
            updateAccessToken(null)
            eventBus.$emit(EVENT_LOGOUT_AFTER)
          }
        } else {
          console.log('jwt decode error')
        }
      }
      state.isLoggedIn = isLoggedIn
      if (!isLoggedIn) {
        this.commit(MUTATION_CLEAR_STATE)
      }
    },
    [MUTATION_SET_PUSHER_APP_KEY](state, appKey) {
      if (state.volatile.pusherAppKey != appKey) {
        if (appKey) {
          // console.log('pusher appKey', appKey)
          eventBus.$emit(EVENT_PUSHER_AVAILABLE, {
            app_key: appKey
          })
        }
        state.volatile.pusherAppKey = appKey
      }
    },
    [MUTATION_SET_GA_CODE](state, code) {
      if (state.volatile.ga != code) {
        if (code) {
          eventBus.$emit(EVENT_GA_AVAILABLE, {
            ga: code
          })
        }
        state.volatile.ga = code
      }
    },
    [MUTATION_REGISTERED](state, data) {
      state.auth.emailAddress = data.email
    },
    [MUTATION_CHANGE_EMAIL](state, email) {
      state.auth.emailAddress = email
    },
    [MUTATION_LOGIN](state, data) {
      this.commit(MUTATION_CLEAR_STATE)
      if (!data.accessToken) {
        throw new Error('accessToken not set!')
      }
      console.log('authorized as ' + data.email)
      state.auth.accessTokenEncrypted = sjcl.encrypt("local.storage", data.accessToken)
      state.auth.emailAddress = data.email
      if (data.user) {
        this.commit(MUTATION_PROFILE_LOADED, data.user);
      }
      updateAccessToken(data.accessToken)
      this.commit(MUTATION_RECALC_LOGIN_STATE)
      eventBus.$emit(EVENT_LOGIN_AFTER)
    },
    [MUTATION_CLEAR_STATE](state) {
      state.auth.accessTokenEncrypted = null
      state.auth.emailAddress = null
      state.friends = []
      state.activeGames = []
      state.friendshipRequests = []
      state.gameInvitations = []
      state.platforms = []
      state.games = []
      state.profile = null
      state.env.user = {}
      state.presets.game = undefined
      state.presets.platform = undefined
      state.advertising.forceAdvertising = false
      state.coinSystem.account = undefined
      state.coinSystem.hideCoinFeature = false
      state.ui.options = {}
    },
    [MUTATION_LOGOUT]() {
      this.commit(MUTATION_CLEAR_STATE)
      updateAccessToken(null)
      this.commit(MUTATION_RECALC_LOGIN_STATE)
      eventBus.$emit(EVENT_LOGOUT_AFTER)
    },
    [MUTATION_ON_ERROR](state, error) {
      if (error.response && error.response && error.response.status === 401) {
        console.log('Unauthorized. Destroying client session...')
        state.auth.accessTokenEncrypted = null
        this.commit(MUTATION_RECALC_LOGIN_STATE)
      }
    },
    [MUTATION_START_LOADING](state) {
      state.loadingCounter++;
      state.isLoading = true;
    },
    [MUTATION_STOP_LOADING](state) {
      state.loadingCounter--;
      if (state.loadingCounter <= 0) {
        state.isLoading = false;
      }
    },
    [MUTATION_RAISE_NOTIFICATION](state, notification) {
      state.notification = notification
    },
    [MUTATION_RAISE_NEWSLETTER_MODAL](state) {
      state.newsletterModal = true
    },
    [MUTATION_DISMISS_NOTIFICATION](state) {
      state.notification = undefined
    },
    [MUTATION_ACTIVE_GAMES_LOADED](state, games) {
      state.activeGames = games;
    },
    [MUTATION_PLATFORMS_LOADED](state, platforms) {
      state.platforms = platforms;
    },
    [MUTATION_GAMES_LOADED](state, games) {
      state.games = games;
    },
    [MUTATION_FRIENDS_LOADED](state, friends) {
      if (friends === undefined || friends === null) {
        console.log('Ignoring empty commit', MUTATION_FRIENDS_LOADED);
        return;
      }
      state.friends = friends
    },
    [MUTATION_FRIENDSHIP_REQUESTS_LOADED](state, requestUsers) {
      if (requestUsers === undefined || requestUsers === null) {
        console.log('Ignoring empty commit', MUTATION_FRIENDSHIP_REQUESTS_LOADED);
        return;
      }
      state.friendshipRequests = requestUsers
    },
    [MUTATION_GAME_INVITATIONS_LOADED](state, gameInvitations) {
      if (gameInvitations === undefined || gameInvitations === null) {
        console.log('Ignoring empty commit', MUTATION_GAME_INVITATIONS_LOADED);
        return;
      }
      state.gameInvitations = gameInvitations
    },
    [MUTATION_PROFILE_LOADED](state, profile) {
      state.profile = profile;
    },
    [MUTATION_PERFORM_SUBSCRIPION](state, subscription) {
      state.performSubscription = subscription;
    },
    [MUTATION_CANCELED_OPEN_SUBSCRIPION](state) {
      state.performSubscription = undefined;
    },
    [MUTATION_SHOW_PREMIUM_DIALOG](state) {
      state.showPremiumDialog = true;
    },
    [MUTATION_HIDE_PREMIUM_DIALOG](state) {
      state.showPremiumDialog = false;
    },
    [MUTATION_SHOW_COINS_DIALOG](state) {
      state.showCoinsDialog = true;
    },
    [MUTATION_HIDE_COINS_DIALOG](state) {
      state.showCoinsDialog = false;
    },
    [MUTATION_SHOW_ONLINE_IDS_DIALOG](state) {
      state.showOnlineIdsDialog = true;
    },
    [MUTATION_HIDE_ONLINE_IDS_DIALOG](state) {
      state.showOnlineIdsDialog = false;
    },
    [MUTATION_SET_MISSING_USER_DATA](state, data) {
      state.missingUserData = data;
    },
    [MUTATION_SHOW_USER_COUNTRY_DIALOG](state) {
      state.showUserCountryDialog = true;
    },
    [MUTATION_HIDE_USER_COUNTRY_DIALOG](state) {
      state.showUserCountryDialog = false;
    },
    [MUTATION_SHOW_CHECKOUT_DIALOG](state, data) {
      state.checkoutDialogData = data;
    },
    [MUTATION_HIDE_CHECKOUT_DIALOG](state) {
      state.checkoutDialogData = false;
    },
    [MUTATION_PRESET_GAME](state, gameCode) {
      state.presets.game = gameCode;
    },
    [MUTATION_PRESET_PLATFORM](state, platformCode) {
      state.presets.platform = platformCode;
    },
    [MUTATION_GAMEINSTANCE_UPDATED](state, gameInstance) {
      var found = false;
      state.activeGames.forEach((activeGame) => {
        if (activeGame.uid == gameInstance.uid) {
          // console.log('Updating', activeGame)
          found = true;
          activeGame.state = gameInstance.state;
          activeGame.yourUnreadChatMessages = gameInstance.yourUnreadChatMessages;
        }
      });
      if (found == false) {
        console.log('Game ' + gameInstance.uid + ' updated, but not found in store.')
      }
    },
    [MUTATION_CHAT_GROUP_STATE_UPDATED](state, event) {
      var found = false;
      state.activeGames.forEach((activeGame) => {
        if (activeGame.chatUid == event.chatGroupName) {
          if (event.unreadMessages || event.unreadMessages === 0) {
            activeGame.yourUnreadChatMessages = event.unreadMessages;
          }
          found = true;
        }
      });
      if (found == false) {
        console.log('Chat ' + event.chatGroupName + ' updated, but not found in store.')
      }
    },
    [MUTATION_ADD_GAME_INSTANCE_INVITATION](state, toAdd) {
      var found = false;
      state.gameInvitations.forEach((invitation) => {
        if (invitation.game.uid == toAdd.game.uid) {
          found = true;
        }
      });
      if (found == false) {
        console.log('Invitation to game uid ' + toAdd.game.uid + ' not found, adding.')
        state.gameInvitations.push(toAdd);
      }
    },
    [MUTATION_REMOVE_GAME_INSTANCE_INVITATION](state, removed) {
      var found = false;
      state.gameInvitations = state.gameInvitations.filter((invitation) => {
        if (invitation.game.uid == removed.game.uid) {
          found = true;
          return false;
        }
        return true;
      });
      if (found == false) {
        console.log('Invitation to game uid ' + removed.game.uid + ' not found, ignoring.')
      }
    },
    [MUTATION_EXT_NATIVE_APP_READY](state, event) {
      state.nativeApp.ready = true;
      state.nativeApp.type = event.type;
    },
    [MUTATION_EXT_NATIVE_APP_NOTIFICATION_INFO](state, data) {
      state.nativeApp.notificationsEnabled = (data.granted == true);
    },
    [MUTATION_EXT_NATIVE_APP_PUSH_NOTIFICATION_INFO](state, data) {
      // also available: data.granted as boolean
      if (data.token !== undefined) {
        state.nativeApp.pushToken = data.token;
        this.dispatch(ACTION_CLAIM_PUSH_TOKEN)
      }
    },
    [MUTATION_UPDATE_COOKIE_CONSENT_STATE](state, data) {
      if (typeof data != 'object') {
        throw new Error('new cookie consent state is not an object!: ' + typeof data)
      }
      state.cookieConsent = data
    }
  },
  actions: {
    [ACTION_INIT]({state}) {
      this.commit(MUTATION_SET_PUSHER_APP_KEY, state.env.global.pusher ? state.env.global.pusher.app_key : null)
      this.dispatch(ACTION_LOAD_ALL_USER_INFORMATION)
    },

    [ACTION_LOAD_PLATFORMS]({ commit }) {
      Vue.prototype.$api.loadPlatforms()
        .then(response => {
          commit(MUTATION_PLATFORMS_LOADED, response.data);
        })
        .catch(error => {
          commit(MUTATION_PLATFORMS_LOADED, []);
          $common.onError(error)
        });
    },

    [ACTION_LOAD_GAMES]({ commit, state }) {
      if (!state.isLoggedIn) {
        return
      }

      Vue.prototype.$api.loadGames()
        .then(response => {
          commit(MUTATION_GAMES_LOADED, response.data);
        })
        .catch(error => {
          $common.onError(error)
        });
    },

    [ACTION_LOAD_PUBLIC_ALL]() {
      Vue.prototype.$api.getPublicAll()
        .then(response => {
          this.dispatch(ACTION_COMPOSITE_DATA_LOADED, response.data)
        })
        .catch(error => {
          $common.onError(error)
        });
    },

    [ACTION_LOAD_ALL_USER_INFORMATION]({ state }) {
      if (!state.isLoggedIn) {
        this.dispatch(ACTION_LOAD_PUBLIC_ALL) // Notice: The envs are also provided in the loadAllUserInformation API, this is for the public access only
        return
      }

      Vue.prototype.$api
        .loadAllUserInformation()
        .then(response => {
          this.dispatch(ACTION_COMPOSITE_DATA_LOADED, response.data)
        })
        .catch(error => {
          $common.onError(error)
        });
    },

    [ACTION_LOAD_FRIENDS]({commit}) {
      if (this.friendsLoadStartAt && new Date().getTime() - this.friendsLoadStartAt.getTime() < 10.000) {
        // Ignore reload within 10 seconds
        return
      }
      this.friendsLoadStartAt = new Date()
      return new Promise((resolve, reject) => {
        Vue.prototype.$api.getFriends()
          .then(response => {
            commit(MUTATION_FRIENDS_LOADED, response.data.friends);
            commit(MUTATION_FRIENDSHIP_REQUESTS_LOADED, response.data.friendshipRequests);
            resolve();
          })
          .catch(error => {
            console.log('Load friends failed', error)
            reject(error);
          });
      })
    },

    [ACTION_LOAD_COIN_ACCOUNT]({commit}) {
      return new Promise((resolve, reject) => {
        Vue.prototype.$api.getCoinAccount()
          .then(response => {
            commit(MUTATION_COIN_ACCOUNT_LOADED, response.data);
            resolve();
          })
          .catch(error => {
            console.log('Load coin account failed', error)
            reject(error);
          });
      })
    },

    [ACTION_RELOAD_ACTIVE_GAMES]({ commit, state }) {
      if (state.isLoggedIn) {
        console.log('Reloading active games');
        Vue.prototype.$api.getGameInstances(['created', 'active'])
          .then(response => {
            commit(MUTATION_ACTIVE_GAMES_LOADED, response.data);
          })
          .catch(error => {
            $common.onError(error)
          });
        }
      },

    [ACTION_RELOAD_DATA]({ dispatch }) {
      Vue.nextTick(() => {
        dispatch(ACTION_LOAD_ALL_USER_INFORMATION);
      })
    },

    [ACTION_LOAD_PROFILE]({commit}) {
      return new Promise((resolve, reject) => {
        Vue.prototype.$api.getProfile()
          .then(response => {
            commit(MUTATION_PROFILE_LOADED, response.data);
            resolve()
          })
          .catch((err) => {
            reject(err)
          })
      })
    },

    [ACTION_CLAIM_PUSH_TOKEN]({state}) {
      if (state.isLoggedIn && state.nativeApp.pushToken) {
        Vue.prototype.$api
          .claimPushToken(state.nativeApp.type, state.nativeApp.pushToken)
          .then(() => {
          })
          .catch(error => {
            $common.onError(error)
          });
      }
    },

    [ACTION_COMPOSITE_DATA_LOADED]({commit}, data) {
      if (data.user) {
        commit(MUTATION_PROFILE_LOADED, data.user);
      }
      if (data.games) {
        commit(MUTATION_GAMES_LOADED, data.games);
      }
      if (data.platforms) {
        commit(MUTATION_PLATFORMS_LOADED, data.platforms);
      }
      if (data.activeGames) {
        commit(MUTATION_ACTIVE_GAMES_LOADED, data.activeGames);
      }
      if (data.friends) {
        commit(MUTATION_FRIENDS_LOADED, data.friends);
      }
      if (data.friendshipRequests) {
        commit(MUTATION_FRIENDSHIP_REQUESTS_LOADED, data.friendshipRequests);
      }
      if (data.gameInvitations) {
        commit(MUTATION_GAME_INVITATIONS_LOADED, data.gameInvitations);
      }
      if (data.env) {
        commit(MUTATION_ENV_LOADED, data.env);
      }
      if (data.advertising) {
        commit(MUTATION_ADVERTISING_LOADED, data.advertising);
      }
      if (data.coinAccount) {
        commit(MUTATION_COIN_ACCOUNT_LOADED, data.coinAccount);
      }
    }
  }
})

eventBus.$on(EVENT_GAMEINSTANCE_UPDATED, (game) => {
  store.commit(MUTATION_GAMEINSTANCE_UPDATED, game);
});

eventBus.$on(EVENT_SHOW_PREMIUM_OPTIONS, (e) => {
  store.commit(MUTATION_SHOW_PREMIUM_DIALOG, e);
});

eventBus.$on(EVENT_SHOW_COINS_DIALOG, (e) => {
  store.commit(MUTATION_SHOW_COINS_DIALOG, e);
});

eventBus.$on(EVENT_SHOW_ONLINE_IDS_DIALOG, (e) => {
  store.commit(MUTATION_SHOW_ONLINE_IDS_DIALOG, e);
});

eventBus.$on(EVENT_PUSHER_EVENT_RECEIVED, (e) => {
  // console.log('Pusher event received', e)
  if (e.type == 'ChatGroupUserStateUpdated') {
    store.commit(MUTATION_CHAT_GROUP_STATE_UPDATED, e.event);
  } else if (e.type == 'GameinstanceInvitationAvailable') {
    store.commit(MUTATION_ADD_GAME_INSTANCE_INVITATION, e.event.model);
  } else if (e.type == 'GameinstanceInvitationDeleted') {
    store.commit(MUTATION_REMOVE_GAME_INSTANCE_INVITATION, e.event.model);
  }
});

eventBus.$on(EVENT_EXT_NATIVE_APP_READY, (e) => {
  store.commit(MUTATION_EXT_NATIVE_APP_READY, e);
});

eventBus.$on(EVENT_EXT_NATIVE_APP_NOTIFICATION_INFO, (e) => {
  store.commit(MUTATION_EXT_NATIVE_APP_NOTIFICATION_INFO, e);
});

eventBus.$on(EVENT_EXT_NATIVE_APP_PUSH_NOTIFICATION_INFO, (e) => {
  store.commit(MUTATION_EXT_NATIVE_APP_PUSH_NOTIFICATION_INFO, e);
});

export default store
